import tokenManager from './TokenManager';
import errorRecoveryManager from './ErrorRecoveryManager';
import { trigger } from './events';
import { isAuthDebugEnabled } from './authDebug';

class DebugTester {
  constructor() {
    this.debugMode = isAuthDebugEnabled();
    this.testResults = [];

    if (this.debugMode) {
      console.log('🔧 DebugTester initialized');
      this.setupGlobalDebug();
    }
  }

  debug(message, data = null) {
    if (this.debugMode) {
      console.log(`🔧 DebugTester: ${message}`, data);
    }
  }

  setupGlobalDebug() {
    // Make debug functions available globally for testing
    if (typeof window !== 'undefined') {
      window.debugTester = this;
      window.testAuth = this.testAuthentication.bind(this);
      window.testTokenExpiry = this.testTokenExpiry.bind(this);
      window.testBackgroundMode = this.testBackgroundMode.bind(this);
      window.testErrorRecovery = this.testErrorRecovery.bind(this);
      window.simulateTokenExpiry = this.simulateTokenExpiry.bind(this);
      window.getAuthStats = this.getAuthStats.bind(this);
    }
  }

  // Test 1: Authentication Flow
  async testAuthentication() {
    this.debug('🧪 Testing authentication flow');

    try {
      // Test token manager initialization
      this.debug('Testing TokenManager initialization');
      const authState = tokenManager.getAuthState();
      this.debug('Current auth state', authState);

      // Test token retrieval
      try {
        const token = await tokenManager.getValidToken();
        this.debug('Token retrieved successfully', {
          tokenLength: token?.length,
        });
        this.addTestResult(
          'Authentication',
          'PASS',
          'Token retrieved successfully'
        );
      } catch (error) {
        this.debug('Token retrieval failed', error);
        this.addTestResult('Authentication', 'FAIL', error.message);
      }

      return this.getTestResults();
    } catch (error) {
      this.debug('Authentication test failed', error);
      this.addTestResult('Authentication', 'ERROR', error.message);
      return this.getTestResults();
    }
  }

  // Test 2: Token Expiry Simulation
  async testTokenExpiry() {
    this.debug('🧪 Testing token expiry handling');

    try {
      // Simulate token expiry
      const originalToken = localStorage.getItem('accessToken');
      this.debug('Original token', { tokenLength: originalToken?.length });

      // Clear token to simulate expiry
      localStorage.removeItem('accessToken');
      localStorage.removeItem('tokenExpiry');

      // Try to get valid token (should trigger refresh)
      try {
        const newToken = await tokenManager.getValidToken();
        this.debug('Token refresh successful', {
          tokenLength: newToken?.length,
        });
        this.addTestResult(
          'Token Expiry',
          'PASS',
          'Token refreshed successfully'
        );
      } catch (error) {
        this.debug('Token refresh failed', error);
        this.addTestResult('Token Expiry', 'FAIL', error.message);
      }

      // Restore original token
      if (originalToken) {
        localStorage.setItem('accessToken', originalToken);
      }

      return this.getTestResults();
    } catch (error) {
      this.debug('Token expiry test failed', error);
      this.addTestResult('Token Expiry', 'ERROR', error.message);
      return this.getTestResults();
    }
  }

  // Test 3: Background Mode Testing
  async testBackgroundMode() {
    this.debug('🧪 Testing background mode handling');

    try {
      // Simulate background mode
      const originalHidden = document.hidden;
      Object.defineProperty(document, 'hidden', {
        value: true,
        writable: true,
      });

      // Trigger visibility change event
      document.dispatchEvent(new Event('visibilitychange'));

      this.debug('Background mode simulated');
      this.addTestResult('Background Mode', 'PASS', 'Background mode detected');

      // Restore original state
      Object.defineProperty(document, 'hidden', {
        value: originalHidden,
        writable: true,
      });

      // Simulate return to foreground
      document.dispatchEvent(new Event('visibilitychange'));

      this.debug('Foreground mode restored');
      this.addTestResult('Background Mode', 'PASS', 'Foreground mode detected');

      return this.getTestResults();
    } catch (error) {
      this.debug('Background mode test failed', error);
      this.addTestResult('Background Mode', 'ERROR', error.message);
      return this.getTestResults();
    }
  }

  // Test 4: Error Recovery Testing
  async testErrorRecovery() {
    this.debug('🧪 Testing error recovery');

    try {
      // Test different error types
      const testErrors = [
        { type: 'AUTH_ERROR', error: { code: 'auth/user-token-expired' } },
        {
          type: 'NETWORK_ERROR',
          error: { name: 'NetworkError', message: 'Network error' },
        },
        { type: 'RATE_LIMITED', error: { status: 429, retryAfter: 5 } },
      ];

      for (const testError of testErrors) {
        try {
          const result = await errorRecoveryManager.handleError(
            testError.error,
            'test'
          );
          this.debug(`Error recovery result for ${testError.type}`, result);
          this.addTestResult(
            `Error Recovery - ${testError.type}`,
            'PASS',
            `Recovery attempted: ${result.recovered}`
          );
        } catch (error) {
          this.debug(`Error recovery failed for ${testError.type}`, error);
          this.addTestResult(
            `Error Recovery - ${testError.type}`,
            'FAIL',
            error.message
          );
        }
      }

      return this.getTestResults();
    } catch (error) {
      this.debug('Error recovery test failed', error);
      this.addTestResult('Error Recovery', 'ERROR', error.message);
      return this.getTestResults();
    }
  }

  // Simulate token expiry for testing
  simulateTokenExpiry() {
    this.debug('🧪 Simulating token expiry');

    // Clear current tokens
    localStorage.removeItem('accessToken');
    localStorage.removeItem('tokenExpiry');

    // Trigger auth error event
    trigger('authError', {
      error: 'Token expired',
      code: 'auth/user-token-expired',
    });

    this.debug('Token expiry simulated');
    return 'Token expiry simulation completed';
  }

  // Get authentication statistics
  getAuthStats() {
    const stats = {
      tokenManager: {
        authState: tokenManager.getAuthState(),
        isRefreshing: tokenManager.isCurrentlyRefreshing(),
        queueLength: tokenManager.tokenRefreshQueue?.length || 0,
      },
      localStorage: {
        hasAccessToken: !!localStorage.getItem('accessToken'),
        hasRefreshToken: !!localStorage.getItem('refreshToken'),
        hasTokenExpiry: !!localStorage.getItem('tokenExpiry'),
      },
      errorRecovery: errorRecoveryManager.getErrorStats(),
      testResults: this.getTestResults(),
    };

    this.debug('Auth stats', stats);
    return stats;
  }

  // Helper methods
  addTestResult(testName, status, message) {
    this.testResults.push({
      testName,
      status,
      message,
      timestamp: new Date().toISOString(),
    });
  }

  getTestResults() {
    return [...this.testResults];
  }

  clearTestResults() {
    this.testResults = [];
  }

  // Run all tests
  async runAllTests() {
    this.debug('🧪 Running all debug tests');
    this.clearTestResults();

    await this.testAuthentication();
    await this.testTokenExpiry();
    await this.testBackgroundMode();
    await this.testErrorRecovery();

    const results = this.getTestResults();
    this.debug('All tests completed', results);

    // Log summary
    const passed = results.filter((r) => r.status === 'PASS').length;
    const failed = results.filter((r) => r.status === 'FAIL').length;
    const errors = results.filter((r) => r.status === 'ERROR').length;

    console.log(
      `🔧 Test Summary: ${passed} passed, ${failed} failed, ${errors} errors`
    );

    return results;
  }
}

// Create singleton instance
const debugTester = new DebugTester();

export default debugTester;
